import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { conferenceRoomApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import './BookingModal.css';

// BookingModal Component
// isUserView prop: when true, applies 1-hour advance booking validation (user side only)
function BookingModal({ isOpen, onClose, selectedRoom, onBookingSuccess, isUserView = false }) {
  const [formData, setFormData] = useState({
    startDateTime: null,
    endDateTime: null,
    purpose: '',
    attendeeCount: '',
    // Recurring booking fields (always weekly)
    isRecurring: false,
    daysOfWeek: [],
    recurringEndDate: null
  });

  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch available slots when start date changes
  useEffect(() => {
    if (formData.startDateTime && selectedRoom) {
      fetchAvailableSlots(formData.startDateTime);
    }
  }, [formData.startDateTime, selectedRoom]);

  // Fetch booked slots for the selected date
  const fetchAvailableSlots = async (date) => {
    try {
      setIsLoading(true);
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      const response = await conferenceRoomApi.getAvailableSlots(selectedRoom.id, dateStr);
      setAvailableSlots(response.data || response || []);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setAvailableSlots([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if time slot is available
  const isTimeSlotAvailable = (startTime, endTime) => {
    if (!availableSlots || !Array.isArray(availableSlots) || availableSlots.length === 0) return true;

    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();

    // Check if selected time overlaps with any booked slot
    for (const slot of availableSlots) {
      const slotStart = new Date(slot.startTime).getTime();
      const slotEnd = new Date(slot.endTime).getTime();

      // If there's any overlap, slot is unavailable
      if ((start >= slotStart && start < slotEnd) ||
          (end > slotStart && end <= slotEnd) ||
          (start <= slotStart && end >= slotEnd)) {
        return false;
      }
    }
    return true;
  };

  // Check if the room type is Medical (exempt from 1-hour advance booking rule)
  const isMedicalRoom = selectedRoom?.roomType?.toLowerCase() === 'medical';

  // Filter time slots - Hide past times and for users, hide times within next hour (except for Medical rooms)
  const filterPassedTime = (time) => {
    const currentDate = new Date();
    const selectedDate = new Date(time);

    // Don't allow past times
    if (selectedDate < currentDate) {
      return false;
    }

    // For user view, don't allow times within the next hour (except for Medical rooms)
    if (isUserView && !isMedicalRoom) {
      const oneHourFromNow = new Date(currentDate.getTime() + 60 * 60 * 1000);
      return selectedDate >= oneHourFromNow;
    }

    return true;
  };

  // Check if a specific time is booked
  const isTimeBooked = (time) => {
    if (!availableSlots || !Array.isArray(availableSlots) || availableSlots.length === 0) return false;

    const selectedTime = new Date(time);

    for (const slot of availableSlots) {
      const slotStart = new Date(slot.startTime);
      const slotEnd = new Date(slot.endTime);

      // Check if the time falls within a booked slot
      if (selectedTime >= slotStart && selectedTime < slotEnd) {
        return true;
      }
    }
    return false;
  };

  // Get booked times to grey out in the picker
  const getExcludedTimes = () => {
    if (!formData.startDateTime || !availableSlots || !Array.isArray(availableSlots) || availableSlots.length === 0) {
      return [];
    }

    const excludedTimes = [];
    const selectedDate = formData.startDateTime;
    const dateStr = selectedDate.toISOString().split('T')[0];

    // Generate 30-minute intervals for the selected day
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeToCheck = new Date(selectedDate);
        timeToCheck.setHours(hour, minute, 0, 0);

        // Check if this time is within any booked slot
        if (isTimeBooked(timeToCheck)) {
          excludedTimes.push(timeToCheck);
        }
      }
    }

    return excludedTimes;
  };

  // Handle date picker change with validation
  const handleDateTimeChange = (field, date) => {
    if (field === 'startDateTime' && date && isTimeBooked(date)) {
      toast.warning('⚠️ This time slot is already booked! Please select another time.', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return; // Don't allow selection of booked time
    }
    handleChange(field, date);
  };

  // Filter end time - must be after start time
  const filterEndTime = (time) => {
    if (!formData.startDateTime) return true;

    const selectedEndTime = new Date(time);
    const startTime = new Date(formData.startDateTime);

    // End time must be after start time
    return selectedEndTime > startTime;
  };

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user makes changes
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle duration preset click
  const handleDurationPreset = (minutes) => {
    if (!formData.startDateTime) {
      toast.warning('Please select a start date and time first', {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }

    const endDateTime = new Date(formData.startDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + minutes);

    handleChange('endDateTime', endDateTime);

    toast.success(`End time set to ${minutes} minutes after start time`, {
      position: "top-center",
      autoClose: 2000,
    });
  };

  // Calculate duration for display
  const calculateDuration = () => {
    if (!formData.startDateTime || !formData.endDateTime) return null;

    const start = new Date(formData.startDateTime);
    const end = new Date(formData.endDateTime);
    const diffMs = end - start;

    if (diffMs <= 0) return null;

    const diffMins = Math.floor(diffMs / 60000);
    const days = Math.floor(diffMins / 1440);
    const hours = Math.floor((diffMins % 1440) / 60);
    const minutes = diffMins % 60;

    let durationStr = '';
    if (days > 0) durationStr += `${days} day${days > 1 ? 's' : ''} `;
    if (hours > 0) durationStr += `${hours} hour${hours > 1 ? 's' : ''} `;
    if (minutes > 0) durationStr += `${minutes} min${minutes > 1 ? 's' : ''}`;

    return durationStr.trim() || 'Less than 1 minute';
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.startDateTime) {
      newErrors.startDateTime = 'Please select start date and time';
    }

    if (!formData.endDateTime) {
      newErrors.endDateTime = 'Please select end date and time';
    }

    if (!formData.purpose || formData.purpose.trim() === '') {
      newErrors.purpose = 'Please enter the purpose of booking';
    }

    // Validate attendee count
    if (formData.attendeeCount && formData.attendeeCount !== '') {
      const count = parseInt(formData.attendeeCount);
      if (isNaN(count) || count <= 0) {
        newErrors.attendeeCount = 'Attendee count must be a positive number';
      } else if (selectedRoom?.capacity && count > selectedRoom.capacity) {
        newErrors.attendeeCount = `Number of attendees (${count}) exceeds room capacity (${selectedRoom.capacity})`;
      }
    }

    // User-side validation: Booking must be at least 1 hour in advance (except for Medical rooms)
    if (isUserView && !isMedicalRoom && formData.startDateTime) {
      const now = new Date();
      const start = new Date(formData.startDateTime);
      const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now

      if (start < oneHourFromNow) {
        newErrors.startDateTime = 'Booking must be made at least 1 hour in advance. Please select a later time.';
      }
    }

    // Check if end time is valid
    if (formData.startDateTime && formData.endDateTime) {
      const start = new Date(formData.startDateTime);
      const end = new Date(formData.endDateTime);

      if (end <= start) {
        newErrors.endDateTime = 'End time must be after start time';
      }

      // Check if slot is available
      if (!isTimeSlotAvailable(start, end)) {
        newErrors.startDateTime = 'This time slot overlaps with an existing booking. Please choose another time.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Format datetime for backend (local timezone without UTC conversion)
  const formatDateTimeForBackend = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      if (formData.isRecurring) {
        // Recurring booking
        const recurringData = {
          roomId: selectedRoom.id,
          startTime: formatDateTimeForBackend(formData.startDateTime),
          endTime: formatDateTimeForBackend(formData.endDateTime),
          notes: formData.purpose,
          attendeeCount: formData.attendeeCount ? parseInt(formData.attendeeCount) : null,
          daysOfWeek: formData.daysOfWeek,
          endDate: formData.recurringEndDate ? formData.recurringEndDate.toISOString().split('T')[0] : null
        };

        const response = await conferenceRoomApi.createRecurringBooking(recurringData);

        toast.success('Recurring booking created! All bookings are awaiting admin approval.', {
          position: "top-right",
          autoClose: 4000,
        });
      } else {
        // Single booking
        const bookingData = {
          roomId: selectedRoom.id,
          startTime: formatDateTimeForBackend(formData.startDateTime),
          endTime: formatDateTimeForBackend(formData.endDateTime),
          notes: formData.purpose,
          attendeeCount: formData.attendeeCount ? parseInt(formData.attendeeCount) : null
        };

        await conferenceRoomApi.createBooking(bookingData);

        toast.success('Booking request submitted! Awaiting admin approval.', {
          position: "top-right",
          autoClose: 4000,
        });
      }

      // Reset form and close modal
      resetForm();
      onBookingSuccess && onBookingSuccess();
      onClose();

    } catch (error) {
      // Extract error message from various possible formats
      let errorMsg = 'Failed to book room';

      if (error.response) {
        // Backend returned an error response
        const status = error.response.status;
        const data = error.response.data;

        if (status === 409) {
          // Conflict error - backend returns plain string message
          errorMsg = typeof data === 'string' ? data : (data?.message || 'Time slot conflict detected. Please choose a different time or date.');
        } else if (typeof data === 'string') {
          // Plain string error message
          errorMsg = data;
        } else if (data?.message) {
          // Error object with message property
          errorMsg = data.message;
        } else if (data?.error) {
          // Error object with error property
          errorMsg = data.error;
        }
      } else if (error.message) {
        // Network or other error
        errorMsg = error.message;
      }

      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      startDateTime: null,
      endDateTime: null,
      purpose: '',
      attendeeCount: '',
      isRecurring: false,
      daysOfWeek: [],
      recurringEndDate: null
    });
    setErrors({});
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const duration = calculateDuration();
  const isAvailable = formData.startDateTime && formData.endDateTime &&
                      isTimeSlotAvailable(formData.startDateTime, formData.endDateTime);

  return (
    <div className="booking-modal-overlay" onClick={handleClose}>
      <div className="booking-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="booking-modal-header">
          <h2>Book Conference Room</h2>
          <button className="booking-modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="booking-room-info">
          <h3>{selectedRoom?.roomName}</h3>
          <p>
            <span className="info-label">Type:</span> {selectedRoom?.roomType} |
            <span className="info-label"> Location:</span> {selectedRoom?.tower}, {selectedRoom?.floor}
            {selectedRoom?.capacity && (
              <>
                {' | '}
                <span className="info-label"> Capacity:</span> {selectedRoom.capacity} people
              </>
            )}
          </p>
          {selectedRoom?.amenities && selectedRoom.amenities.length > 0 && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px'
            }}>
              <span style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#666',
                marginRight: '8px'
              }}>
                Amenities:
              </span>
              {(typeof selectedRoom.amenities === 'string' ? selectedRoom.amenities.split(',') : selectedRoom.amenities).map((amenity, index, arr) => (
                <span key={index} style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  margin: '2px',
                  fontSize: '12px',
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  borderRadius: '12px',
                  border: '1px solid #bbdefb'
                }}>
                  {amenity.trim()}
                </span>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="booking-form">
          {/* START DATE AND TIME PICKER */}
          <div className="form-group">
            <label htmlFor="startDateTime">
              Start Date & Time <span className="required">*</span>
            </label>
            <DatePicker
              id="startDateTime"
              selected={formData.startDateTime}
              onChange={(date) => handleDateTimeChange('startDateTime', date)}
              showTimeSelect
              timeIntervals={30}
              timeCaption="Time"
              dateFormat="MMMM d, yyyy h:mm aa"
              minDate={new Date()}
              filterTime={filterPassedTime}
              excludeTimes={getExcludedTimes()}
              placeholderText="Select start date and time"
              className={`date-picker-input ${errors.startDateTime ? 'error' : ''}`}
              autoComplete="off"
            />
            {errors.startDateTime && (
              <span className="error-message">{errors.startDateTime}</span>
            )}
            <p className="helper-text">
              {isUserView
                ? (isMedicalRoom
                    ? 'Medical rooms can be booked immediately. Greyed out times are unavailable.'
                    : 'Bookings must be made at least 1 hour in advance. Greyed out times are unavailable.')
                : 'Greyed out times are already booked - click for details'}
            </p>
          </div>

          {/* DURATION PRESETS */}
          {formData.startDateTime && (
            <div className="form-group">
              <label>Quick Duration Selection</label>
              <div style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                marginTop: '8px'
              }}>
                <button
                  type="button"
                  onClick={() => handleDurationPreset(30)}
                  className="duration-preset-btn"
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #4CAF50',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#4CAF50',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#4CAF50';
                    e.target.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.color = '#4CAF50';
                  }}
                >
                  30 min
                </button>
                <button
                  type="button"
                  onClick={() => handleDurationPreset(60)}
                  className="duration-preset-btn"
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #4CAF50',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#4CAF50',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#4CAF50';
                    e.target.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.color = '#4CAF50';
                  }}
                >
                  1 hour
                </button>
                <button
                  type="button"
                  onClick={() => handleDurationPreset(120)}
                  className="duration-preset-btn"
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #4CAF50',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#4CAF50',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#4CAF50';
                    e.target.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.color = '#4CAF50';
                  }}
                >
                  2 hours
                </button>
                <button
                  type="button"
                  onClick={() => handleDurationPreset(240)}
                  className="duration-preset-btn"
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #4CAF50',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#4CAF50',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#4CAF50';
                    e.target.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.color = '#4CAF50';
                  }}
                >
                  4 hours
                </button>
              </div>
              <p className="helper-text">Click to quickly set meeting duration</p>
            </div>
          )}

          {/* BOOKED SLOTS INDICATOR */}
          {availableSlots && availableSlots.length > 0 && formData.startDateTime && (
            <div className="form-group">
              <label>⚠️ Already Booked Slots for This Day</label>
              <div className="booked-slots-list">
                {availableSlots.map((slot, index) => {
                  const startTime = new Date(slot.startTime);
                  const endTime = new Date(slot.endTime);
                  const formatTime = (date) => date.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  });

                  return (
                    <div key={index} className="booked-slot-item">
                      <span className="booked-time-icon">🚫</span>
                      <span className="booked-time-range">
                        {formatTime(startTime)} - {formatTime(endTime)}
                      </span>
                      {slot.purpose && (
                        <span className="booked-purpose">({slot.purpose})</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* END DATE AND TIME PICKER */}
          <div className="form-group">
            <label htmlFor="endDateTime">
              End Date & Time <span className="required">*</span>
            </label>
            <DatePicker
              id="endDateTime"
              selected={formData.endDateTime}
              onChange={(date) => handleChange('endDateTime', date)}
              showTimeSelect
              timeIntervals={30}
              timeCaption="Time"
              dateFormat="MMMM d, yyyy h:mm aa"
              minDate={formData.startDateTime || new Date()}
              filterTime={filterEndTime}
              placeholderText="Select end date and time"
              className={`date-picker-input ${errors.endDateTime ? 'error' : ''}`}
              autoComplete="off"
              disabled={!formData.startDateTime}
            />
            {errors.endDateTime && (
              <span className="error-message">{errors.endDateTime}</span>
            )}
            <p className="helper-text">
              {!formData.startDateTime
                ? 'Please select start time first'
                : 'Can span multiple days (e.g., 2 days, 3 days, etc.)'}
            </p>
          </div>

          {/* DURATION DISPLAY */}
          {duration && (
            <div className="form-group">
              <label>Total Duration</label>
              <div className="duration-display">
                <span className="duration-icon">⏱️</span>
                <span className="duration-text">{duration}</span>
              </div>
            </div>
          )}

          {/* AVAILABILITY INDICATOR */}
          {formData.startDateTime && formData.endDateTime && (
            <div className={`availability-indicator ${isAvailable ? 'available' : 'unavailable'}`}>
              <span className="indicator-icon">
                {isAvailable ? '✓' : '✕'}
              </span>
              <span className="indicator-text">
                {isAvailable
                  ? 'This time slot is available'
                  : 'This time slot overlaps with an existing booking'}
              </span>
            </div>
          )}

          {/* PURPOSE */}
          <div className="form-group">
            <label htmlFor="purpose">
              Purpose <span className="required">*</span>
            </label>
            <textarea
              id="purpose"
              value={formData.purpose}
              onChange={(e) => handleChange('purpose', e.target.value)}
              placeholder="e.g., Team Meeting, Client Presentation, Training Session, Conference"
              rows="3"
              className={errors.purpose ? 'error' : ''}
            />
            {errors.purpose && (
              <span className="error-message">{errors.purpose}</span>
            )}
          </div>

          {/* ATTENDEE COUNT */}
          <div className="form-group">
            <label htmlFor="attendeeCount">
              Number of Attendees
            </label>
            <input
              type="number"
              id="attendeeCount"
              value={formData.attendeeCount}
              onChange={(e) => handleChange('attendeeCount', e.target.value)}
              placeholder="Enter number of attendees"
              min="1"
              max={selectedRoom?.capacity || 999}
              className={errors.attendeeCount ? 'error' : ''}
            />
            {errors.attendeeCount && (
              <span className="error-message">{errors.attendeeCount}</span>
            )}
            {selectedRoom?.capacity && (
              <p className="helper-text">
                Room capacity: {selectedRoom.capacity} {formData.attendeeCount && parseInt(formData.attendeeCount) <= selectedRoom.capacity ? '✓' : ''}
              </p>
            )}
          </div>

          {/* RECURRING BOOKING TOGGLE */}
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isRecurring}
                onChange={(e) => handleChange('isRecurring', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Make this a recurring booking
            </label>
            <p className="helper-text">Schedule this booking to repeat weekly</p>
          </div>

          {/* RECURRING BOOKING OPTIONS */}
          {formData.isRecurring && (
            <div className="recurring-booking-section" style={{
              border: '1px solid #e0e0e0',
              padding: '15px',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9',
              marginBottom: '15px'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>Recurring Booking Settings</h4>

              {/* DAYS OF WEEK */}
              <div className="form-group">
                <label>Select Days of Week <span className="required">*</span></label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map(day => (
                    <label key={day} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      border: formData.daysOfWeek.includes(day) ? '2px solid #4CAF50' : '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: formData.daysOfWeek.includes(day) ? '#e8f5e9' : 'white'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.daysOfWeek.includes(day)}
                        onChange={(e) => {
                          const newDays = e.target.checked
                            ? [...formData.daysOfWeek, day]
                            : formData.daysOfWeek.filter(d => d !== day);
                          handleChange('daysOfWeek', newDays);
                        }}
                        style={{ marginRight: '6px' }}
                      />
                      {day.charAt(0) + day.slice(1).toLowerCase()}
                    </label>
                  ))}
                </div>
                <p className="helper-text">Select which days of the week to repeat this booking</p>
              </div>

              {/* REPEAT TILL DATE */}
              <div className="form-group">
                <label htmlFor="recurringEndDate">
                  Repeat Till Date <span className="required">*</span>
                </label>
                <DatePicker
                  id="recurringEndDate"
                  selected={formData.recurringEndDate}
                  onChange={(date) => handleChange('recurringEndDate', date)}
                  minDate={formData.startDateTime || new Date()}
                  dateFormat="MMMM d, yyyy"
                  placeholderText="Select last date for recurring bookings"
                  className="date-picker-input"
                />
                <p className="helper-text">Bookings will be created weekly on selected days until this date</p>
              </div>
            </div>
          )}

          {/* INFO MESSAGE */}
          <div className="info-message">
            <span className="info-icon">ℹ️</span>
            <span>Your booking request will be sent to admin for approval.</span>
          </div>

          {/* FORM ACTIONS */}
          <div className="booking-form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || (formData.startDateTime && formData.endDateTime && !isAvailable)}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BookingModal;
